'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { PageHeader } from '@/components/common/page-header';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { useOrganization } from '@/contexts/organization-context';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import { Car, PetrohqApiResponseListCar } from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { useCallback, useEffect, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';

export function CarManagement() {
  const { toast } = useToast();
  const [cars, setCars] = useState<Car[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 필터 상태 가져오기
  const { store } = useFilterStore();
  const { getAllStores } = useOrganization();

  const fetchCars = useCallback(async () => {
    setIsLoading(true);
    try {
      // 필터에서 선택된 store 값들을 사용, 없으면 모든 store 사용
      const storeCodeList = store.length > 0 ? store : getAllStores();

      const searchParams = new URLSearchParams();
      storeCodeList.forEach(storeCode => {
        searchParams.append('storeCodeList', storeCode);
      });

      const response = await apiClient.get('master/car', {
        searchParams: searchParams,
      });
      const data = (await response.json()) as PetrohqApiResponseListCar;
      const carData = data.result || [];
      setCars(carData);
    } catch {
      toast({
        title: '조회 실패',
        description: '자동차 데이터를 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [store, toast, getAllStores]);

  // 초기 데이터 로드
  useEffect(() => {
    fetchCars();
  }, []);

  // 조회 버튼 클릭 핸들러
  const handleSearch = () => {
    fetchCars();
  };

  return (
    <ExpandableCard
      breadcrumb={
        <Breadcrumb items={menuBreadcrumbItems('car')} />
      }
      title='자동차 목록'
      fill
      pageHeader={
        <PageHeader
          onSearch={handleSearch}
          isLoading={isLoading}
        />
      }
    >
      {/* 자동차 테이블 */}
      <div
        className='border rounded-lg flex-1 min-h-0'
      >
        <div className='w-full h-full'>
          <RealGridReact
            gridProps={{
              checkBar: { visible: false },
              stateBar: { visible: false },
              edit: { readOnly: true, editable: false },
              sorting: {
                enabled: true,
                showSortOrder: true,
                style: RealGrid.SortStyle.INCLUSIVE,
              },
            }}
            rows={cars}
          >
            <RGDataField fieldName={'no'} dataType={RealGrid.ValueType.TEXT} />
            <RGDataField
              fieldName={'compCd'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'storeCd'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'dealCode'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'dealName'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'carCode'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'carName'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'tell'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'carLimit'}
              dataType={RealGrid.ValueType.NUMBER}
            />
            <RGDataColumn fieldName={'storeCd'} header={{ text: '매장명' }} />
            <RGDataColumn
              fieldName={'dealCode'}
              header={{ text: '거래처코드' }}
              width={70}
            />
            <RGDataColumn
              fieldName={'dealName'}
              header={{ text: '거래처명' }}
            />
            <RGDataColumn fieldName={'carCode'} header={{ text: '차량코드' }} />
            <RGDataColumn fieldName={'carName'} header={{ text: '차량명' }} />
            <RGDataColumn fieldName={'tell'} header={{ text: '전화번호' }} />
            <RGDataColumn
              fieldName={'carLimit'}
              header={{ text: '외상한도액' }}
              width={90}
              numberFormat={'#,##0'}
              styleName={'text-end'}
            />
          </RealGridReact>
        </div>
      </div>
    </ExpandableCard>
  );
}
